import React, { useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react';
import userStore from '../../Stores/User';
import { linkNewEmail, deleteUser, updateUserTldrPreference, updateUserPrivacyMode, getUser, createReactivationCheckoutSession, getUserCharacters } from '../../audiblogsApi';
import CharacterUsageBar from './CharacterUsageBar';
import discoverStore from '../DiscoverNew/store';
import mixpanel from 'mixpanel-browser';
import { useHistory } from 'react-router-dom';
import loadingIcon from "../../Assets/loading.gif";
import { Modal, mergeStyleSets } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { createStripeSessionAndRedirectToCheckout } from '../../stripeLogic';
import { Tooltip } from '@mui/material';
import { isPaidProUser } from '../../constants/plans';
import gamificationStore from '../../Stores/GamificationStore';
import BonusIndicator from '../../Components/BonusIndicator';
import {
  Apple as AppleIcon,
  Android as AndroidIcon,
  RssFeed as RssFeedIcon,
  AccountCircle as AccountCircleIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Paid as PaidIcon,
  Help as HelpIcon,
  ChevronLeft as ChevronLeftIcon,
  Podcasts as PodcastsIcon,
} from '@mui/icons-material';
import Chrome from '../../Assets/chrome.svg';
import Loader from '../../Components/Loader';
import TopNavBar from '../../Components/TopNavBar';
import ShareModal from '../../Components/ShareModal';
import './index.scss';

const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column',
    backgroundColor: 'rgb(66 59 74)',
    width: '300px',
    padding: '10px',
    fontFamily: 'Cabin',
    fontSize: '18px',
    textAlign: 'center',
    minHeight: '108px',
    borderRadius: '14px',
  },
});

const Settings = observer(() => {
  const { logOut, idToken, isSignedIn, user, creditsHd, creditsSd, userFeedUrl, hideChallenges } = userStore;
  const [deletedData, setDeletedData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
  const [modalContent, setModalContent] = useState(null);
  const [addTldr, setAddTldr] = useState(false);
  const [privacyMode, setPrivacyMode] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showReferralLink, setShowReferralLink] = useState(false);
  const [isReferralShareModalOpen, setIsReferralShareModalOpen] = useState(false);
  const [characterData, setCharacterData] = useState(null);
  const [characterLoading, setCharacterLoading] = useState(true);
  const history = useHistory();

  const handleToggleHideChallenges = () => {
    const newValue = !hideChallenges;
    // Store preference in localStorage for now
    localStorage.setItem('hideChallenges', JSON.stringify(newValue));
    userStore.updateHideChallenges(newValue);
  };
  
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
    ? isPaidProUser(userStore.user)
    : false;

  useEffect(() => {
    mixpanel.track('Open Settings Page');
  }, []);

  useEffect(() => {
    if (isSignedIn === false && deletedData === false) {
      history.push(`/signin`);
    } else if (isSignedIn === false && deletedData === true) {
      history.push(`/bye`);
    }
  }, [isSignedIn, idToken]);

  useEffect(() => {
    // Fetch user data to get the current TLDR preference
    if (idToken) {
      getUser(idToken)
        .then(response => {
          if (response.data) {
            setAddTldr(response.data.addTldr);
            setPrivacyMode(response.data.privacyMode || false);
          }
        })
        .catch(error => {
          console.error('Error fetching user data:', error);
        });
    }
    
    // hideChallenges preference is now loaded from localStorage in User store initialization
  }, [idToken]);

  useEffect(() => {
    // Fetch character usage data
    if (idToken) {
      setCharacterLoading(true);
      getUserCharacters(idToken)
        .then(response => {
          if (response.data) {
            setCharacterData(response.data);
            // Check for 500k character usage bonus
            gamificationStore.check500kCharacterUsage(response.data);
          }
        })
        .catch(error => {
          console.error('Error fetching character data:', error);
        })
        .finally(() => {
          setCharacterLoading(false);
        });
    }
  }, [idToken]);

  // Listen for character data updates from gamification bonuses
  useEffect(() => {
    const handleCharacterDataUpdate = (event) => {
      setCharacterData(event.detail);
    };

    window.addEventListener('characterDataUpdated', handleCharacterDataUpdate);
    
    return () => {
      window.removeEventListener('characterDataUpdated', handleCharacterDataUpdate);
    };
  }, []);

  const showCharacterPurchaseModal = () => {
    const CharacterPurchaseModal = () => {
      const [charAmount, setCharAmount] = useState(1);
      const pricePerMillion = 15;
      const totalPrice = (charAmount * pricePerMillion).toFixed(2);
      
      return (
        <>
          <div style={{ color: 'white', marginBottom: '15px', fontWeight: 'bold' }}>
            Purchase Additional Characters
          </div>
          <div style={{ color: 'white', marginBottom: '20px' }}>
            Purchase additional characters that will be used for any overages beyond your plan's allotment.
          </div>
          
          <div style={{ marginBottom: '20px', textAlign: 'left' }}>
            <div style={{ color: 'white', marginBottom: '10px' }}>
              Select amount to purchase:
            </div>
            <input
              type="range"
              min="1"
              max="10"
              value={charAmount}
              onChange={(e) => setCharAmount(Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              color: 'white',
              marginTop: '5px'
            }}>
              <div>{charAmount}M characters</div>
              <div>${totalPrice}</div>
            </div>
            <div style={{ 
              color: '#4CAF50', 
              fontSize: '14px',
              marginTop: '5px' 
            }}>
              ~{Math.round((charAmount * 1000000 / 10000) * 10.5)} minutes of audio
            </div>
          </div>
          
          <button
            onClick={async () => {
              try {
                setIsLoading(true);
                const response = await createStripeSessionAndRedirectToCheckout(userStore.idToken, {
                  mode: 'payment',
                  amount: charAmount * pricePerMillion * 100,
                  currency: 'usd',
                  metadata: {
                    type: 'character_purchase',
                    characters: charAmount * 1000000
                  }
                });
              } catch (error) {
                console.error('Error creating character purchase session:', error);
                setModalContent({
                  title: "Error",
                  message: "Unable to process payment. Please try again or contact support.",
                });
              } finally {
                setIsLoading(false);
              }
            }}
            className="clickable"
            style={{
              backgroundColor: '#1976d2',
              border: 'none',
              color: 'white',
              borderRadius: '9px',
              marginTop: '14px',
              marginRight: '10px',
              fontSize: '16px',
              padding: '8px 16px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {isLoading ? (
              <img src={loadingIcon} style={{ height: '25px' }} alt="loading..." />
            ) : (
              `Purchase for $${totalPrice}`
            )}
          </button>
          
          <button
            onClick={() => {
              window.Intercom('show');
              hideModal();
            }}
            className="clickable"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid lightgrey',
              color: 'white',
              borderRadius: '9px',
              marginTop: '14px',
              fontSize: '16px',
              padding: '2px 8px',
              outline: 'none',
            }}
          >
            Contact Support
          </button>
        </>
      );
    };

    setModalContent({
      customContent: <CharacterPurchaseModal />
    });
    showModal();
  };

  const handleTldrClick = () => {
    if (isProUser) {
      const newValue = !addTldr;
      setAddTldr(newValue);
      updateUserTldrPreference(user.user_id, newValue)
        .then(response => {
          console.log('User record updated:', response.data);
        })
        .catch(error => {
          console.error('Error updating user record:', error);
          // Revert the state if the API call fails
          setAddTldr(!newValue);
        });
    } else {
      // Show modal for non-pro users
      setModalContent({
        title: "Pro Feature", 
        message: "Upgrade to pro to use the summary feature.",
        actionText: "Upgrade Now",
        actionHandler: () => {
          const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
          const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
          
          if (hasStripeCustomerId && !hasActiveSubscription) {
            // User has cancelled subscription - create reactivation checkout
            createReactivationCheckoutSession(userStore.idToken, {}, '/add')
              .then(response => {
                if (response.data.url) {
                  window.location.href = response.data.url;
                } else {
                  window.location.href = '/onboarding/choosevoice';
                }
              })
              .catch(() => {
                window.location.href = '/onboarding/choosevoice';
              });
          } else {
            // User has active subscription - send to customer portal
            window.location.href = userStore.customerPortalUrl;
          }
          hideModal();
        }
      });
      showModal();
    }
  };

  const handlePrivacyModeClick = () => {
    if (isProUser) {
      const newValue = !privacyMode;
      setPrivacyMode(newValue);
      updateUserPrivacyMode(user.user_id, newValue)
        .then(response => {
          console.log('User privacy mode updated:', response.data);
          userStore.updatePrivacyMode(newValue);
        })
        .catch(error => {
          console.error('Error updating user privacy mode:', error);
          // Revert the state if the API call fails
          setPrivacyMode(!newValue);
        });
    } else {
      // Show modal for non-pro users
      setModalContent({
        title: "Pro Feature", 
        message: "Upgrade to pro to use privacy mode.",
        actionText: "Upgrade Now",
        actionHandler: () => {
          const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
          const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
          
          if (hasStripeCustomerId && !hasActiveSubscription) {
            // User has cancelled subscription - create reactivation checkout
            createReactivationCheckoutSession(userStore.idToken, {}, '/add')
              .then(response => {
                if (response.data.url) {
                  window.location.href = response.data.url;
                } else {
                  window.location.href = '/onboarding/choosevoice';
                }
              })
              .catch(() => {
                window.location.href = '/onboarding/choosevoice';
              });
          } else {
            // User has active subscription - send to customer portal
            window.location.href = userStore.customerPortalUrl;
          }
          hideModal();
        }
      });
      showModal();
    }
  };

  const handleEmailClick = () => {
    setShowEmail(true);
    const emailAddress = `user-${user.user_id}@email.audioread.com`;
    navigator.clipboard.writeText(emailAddress);
    
    // Show notification
    const notification = document.createElement('div');
    notification.textContent = 'Email address copied to clipboard!';
    notification.style.position = 'fixed';
    notification.style.top = '20px';
    notification.style.left = '50%';
    notification.style.transform = 'translateX(-50%)';
    notification.style.backgroundColor = '#1976d2';
    notification.style.color = 'white';
    notification.style.padding = '10px 20px';
    notification.style.borderRadius = '5px';
    notification.style.zIndex = '1000';
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  if (user === null) {
    return <Loader />;
  }

  return (
    <div className="settings-container">
      <TopNavBar />
      <Modal
        isOpen={isModalOpen && !isLoading}
        onDismiss={hideModal}
        isBlocking={false}
        isDarkOverlay={true}
        containerClassName={contentStyles.container}
      >
        {modalContent?.customContent || (
          <>
            {modalContent?.title && (
              <div style={{ color: 'white', marginBottom: '15px', fontWeight: 'bold' }}>
                {modalContent.title}
              </div>
            )}
            {modalContent?.message && (
              <div style={{ color: 'white', marginBottom: '20px' }}>
                {modalContent.message}
              </div>
            )}
            {modalContent?.actionText && modalContent?.actionHandler && (
              <button
                onClick={modalContent.actionHandler}
                className="clickable"
                style={{
                  backgroundColor: '#1976d2',
                  border: 'none',
                  color: 'white',
                  borderRadius: '9px',
                  marginTop: '14px',
                  marginRight: '10px',
                  fontSize: '16px',
                  padding: '8px 16px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                {modalContent.actionText}
              </button>
            )}
            <button
              onClick={hideModal}
              className="clickable"
              style={{
                backgroundColor: 'transparent',
                border: '1px solid lightgrey',
                color: 'white',
                borderRadius: '9px',
                marginTop: '14px',
                fontSize: '16px',
                padding: '2px 8px',
                outline: 'none',
              }}
            >
              Cancel
            </button>
          </>
        )}
      </Modal>

      <button 
        onClick={() => {
          discoverStore.changeMode("addArticle");
          window.location.href = "/add";
        }}
        className="back-button"
      >
        <ChevronLeftIcon />
        <span className="back-text">Home</span>
      </button>

      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account and preferences</p>
      </div>

      {/* Preferences Section */}
      <div className="settings-section">
        <h2>Preferences</h2>
        
        <div className="settings-buttons">
          <div className="settings-button toggle">
            <div className="toggle-content">
              <div 
                className={`custom-checkbox ${addTldr ? 'checked' : ''}`}
                onClick={handleTldrClick}
                role="checkbox"
                aria-checked={addTldr}
                aria-label="Add TLDR"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTldrClick();
                  }
                }}
              >
                {addTldr && (
                  <svg 
                    className="check-icon" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" 
                      fill="currentColor"
                    />
                  </svg>
                )}
              </div>
              <span>Add Summary</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://help.audioread.com/en/articles/11060582-how-to-get-summaries-of-your-articles', '_blank');
                }}
                className="help-button"
                style={{
                  background: 'none',
                  border: '1px solid #ccc',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  marginLeft: '8px',
                  padding: 0,
                  fontSize: '12px',
                  color: '#ccc',
                  outline: 'none',
                }}
                title="Learn more about summaries"
              >
                ?
              </button>
            </div>
          </div>

          <div className="settings-button toggle">
            <div className="toggle-content">
              <div 
                className={`custom-checkbox ${privacyMode ? 'checked' : ''}`}
                onClick={handlePrivacyModeClick}
                role="checkbox"
                aria-checked={privacyMode}
                aria-label="Privacy Mode"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePrivacyModeClick();
                  }
                }}
              >
                {privacyMode && (
                  <svg 
                    className="check-icon" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" 
                      fill="currentColor"
                    />
                  </svg>
                )}
              </div>
              <span>Privacy Mode</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://help.audioread.com/en/articles/12020770-privacy-mode-maximum-protection-for-your-articles', '_blank');
                }}
                className="help-button"
                style={{
                  background: 'none',
                  border: '1px solid #ccc',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  marginLeft: '8px',
                  padding: 0,
                  fontSize: '12px',
                  color: '#ccc',
                  outline: 'none',
                }}
                title="Learn more about privacy mode"
              >
                ?
              </button>
            </div>
          </div>

          {hideChallenges ? (
            <Link to="/PreferredVoices" className="settings-button">
              <RecordVoiceOverIcon />
              <span>Choose Voice</span>
            </Link>
          ) : (
            <BonusIndicator action={gamificationStore.actions.CHOOSE_VOICE}>
              <Link to="/PreferredVoices" className="settings-button">
                <RecordVoiceOverIcon />
                <span>Choose Voice</span>
              </Link>
            </BonusIndicator>
          )}
        </div>
      </div>

      {/* Setup Section */}
      <div className="settings-section">
        <h2>Setup & Integration</h2>
        
        <div className="settings-buttons">
          {userFeedUrl && (
            <BonusIndicator action={gamificationStore.actions.PODCAST_SETUP}>
              <Link
                to={`/setupnew?feed_url=${encodeURIComponent(userFeedUrl)}`}
                className="settings-button featured"
              >
                <PodcastsIcon />
                <span>Connect Podcast App</span>
              </Link>
            </BonusIndicator>
          )}

          <BonusIndicator action={gamificationStore.actions.INSTALL_EXTENSION} manualCompletion={true}>
            <button
              onClick={() => {
                window.open('https://chrome.google.com/webstore/detail/audiblogs-send-as-podcast/fblchjbbkkaoobkhhbloehgkpdlomido', '_blank');
              }}
              className="settings-button"
            >
              <img src={Chrome} alt="Chrome" />
              <span>Setup on Browser</span>
            </button>
          </BonusIndicator>

          <BonusIndicator action={gamificationStore.actions.CONNECT_PHONE} manualCompletion={true}>
            <button
              onClick={() => {
                window.open('/onboarding/ios', '_blank');
              }}
              className="settings-button"
            >
              <AppleIcon />
              <span>Setup on Apple</span>
            </button>
          </BonusIndicator>

          <BonusIndicator action={gamificationStore.actions.CONNECT_PHONE} manualCompletion={true}>
            <button
              onClick={() => {
                window.open('/onboarding/android', '_blank');
              }}
              className="settings-button"
            >
              <AndroidIcon />
              <span>Setup on Android</span>
            </button>
          </BonusIndicator>

          <BonusIndicator action={gamificationStore.actions.CONVERT_FIRST_EMAIL} className="full-width" manualCompletion={true}>
            <button 
              onClick={handleEmailClick} 
              className="settings-button full-width"
            >
              <ForwardToInboxIcon />
              <span>Listen to Emails</span>
            </button>
          </BonusIndicator>

          {showEmail && user && user.user_id && (
            <div className="email-info">
              <p>Forward emails to:</p>
              <a href={`mailto:user-${user.user_id}@email.audioread.com`}>
                user-{user.user_id}@email.audioread.com
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Support & Community Section */}
      <div className="settings-section">
        <h2>Support & Community</h2>
        
        <div className="settings-buttons">
          <button
            onClick={() => {
              setIsReferralShareModalOpen(true);
            }}
            className="settings-button"
          >
            <span>🎁 Give a month, get 1m characters</span>
          </button>

          {showReferralLink && user && user.user_id && (
            <div className="email-info">
              <p>Your referral link:</p>
              <a href={`${window.location.origin}/referral/${user.user_id}`}>
                {`${window.location.origin}/referral/${user.user_id}`}
              </a>
            </div>
          )}

          <a 
            href="https://audioread.tolt.io/" 
            target="_blank"
            className="settings-button"
          >
            <PaidIcon />
            <span>Earn Lifetime Commission</span>
          </a>

          <BonusIndicator action={gamificationStore.actions.LEAVE_REVIEW} manualCompletion={true}>
            <button
              onClick={() => {
                window.open('https://chrome.google.com/webstore/detail/audiblogs-send-as-podcast/fblchjbbkkaoobkhhbloehgkpdlomido/reviews', '_blank');
              }}
              className="settings-button"
            >
              <span style={{ alignItems: 'left', textAlign: 'left' }}> ⭐&nbsp;&nbsp;&nbsp;&nbsp;Leave a Review</span>
            </button>
          </BonusIndicator>

          <button 
            onClick={() => window.Intercom('show')}
            className="settings-button"
          >
            <HelpIcon />
            <span>Help</span>
          </button>
        </div>
      </div>

      {/* Gamification Progress Section */}
      <div className="settings-section">
        <div className="section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h2>Bonus Progress</h2>
            <Tooltip title="Learn about bonus challenges" placement="top">
              <a 
                href="https://help.audioread.com/en/articles/12050499-unlock-free-extra-characters-with-audioread-bonus-challenges"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  color: '#757575',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#1976d2';
                  e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#757575';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <HelpIcon fontSize="small" />
              </a>
            </Tooltip>
          </div>
          <button
            onClick={handleToggleHideChallenges}
            className="hide-challenges-toggle"
            title={hideChallenges ? "Show challenges" : "Hide challenges"}
          >
            {hideChallenges ? "👁️ Show" : "🙈 Hide"} Challenges
          </button>
        </div>
        <div className="settings-buttons">
          <div className="gamification-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${gamificationStore.getProgressPercentage()}%` }}
              ></div>
            </div>
            <div className="progress-text">
              {gamificationStore.getCompletedActionsCount()} of {gamificationStore.getTotalActionsCount()} bonuses earned
              <div className="progress-subtext">
                Total free characters earned: +{(() => {
                  let total = 0;
                  // Add up all completed action bonuses
                  gamificationStore.completedActions.forEach(action => {
                    total += gamificationStore.bonusAmounts[action] || 0;
                  });
                  // Add referral bonuses
                  total += (characterData?.referral_count || 0) * 1000000;
                  return Math.floor(total / 1000);
                })()}k
              </div>
            </div>
            {!gamificationStore.isActionCompleted(gamificationStore.actions.USE_500K_CHARS) && characterData && (
              <div className="char-usage-progress">
                <div className="char-usage-text">
                  Use 500k characters in first 30 days: {Math.floor((characterData.total_characters || 0) / 1000)}k / 500k
                </div>
                <div className="char-usage-bar">
                  <div 
                    className="char-usage-fill" 
                    style={{ width: `${Math.min(((characterData.total_characters || 0) / 500000) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
            {gamificationStore.getProgressPercentage() === 100 && !gamificationStore.hasReceivedCompletionBonus && (
              <div className="completion-bonus">
                🎉 +250k completion bonus available!
              </div>
            )}

            {/* Referral Stats */}
            <div className="referral-stats">
              <div className="referral-stats-header">
                <span>🎁 Referral Rewards</span>
              </div>
              <div className="referral-stats-content">
                <div className="referral-stat">
                  <span className="stat-label">Referrals Made:</span>
                  <span className="stat-value">{characterData?.referral_count || 0}</span>
                </div>
                <div className="referral-stat">
                  <span className="stat-label">Characters Earned:</span>
                  <span className="stat-value">+{(characterData?.referral_count || 0) * 1000000} chars</span>
                </div>
                <div className="referral-stat">
                  <span className="stat-label">Your Referral Link:</span>
                  <span className="stat-value" style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                    {`${window.location.origin}/referral/${user.user_id}`}
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Account & Billing Section */}
      <div className="settings-section">
        <h2>Account & Billing</h2>

                <div className="settings-buttons">
          {(user.stripe_customer_id || user.customer_id || user.subscriptions) && (
            <>
              <CharacterUsageBar
                totalCharacters={characterData?.total_characters || 0}
                characterLimit={characterData?.character_limit || 1000000}
                extraCredits={characterData?.extra_credits || 0}
                isLoading={characterLoading}
              />
              <button
                onClick={showCharacterPurchaseModal}
                className="settings-button primary character-purchase-button"
              >
                <span>Buy More Characters</span>
                <a 
                  href="https://help.audioread.com/en/articles/11783511-how-extra-characters-work" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="help-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <HelpIcon />
                </a>
              </button>
            </>
          )}

          {user.subscriptions && (user.subscriptions.hd || user.subscriptions.sd) && (
            <Link to="/subscribe" className="settings-button">
              <span>Buy Credits</span>
            </Link>
          )}

          {userStore.customerPortalUrl ? (
            <a
              href={userStore.customerPortalUrl}
              target="_blank"
              className="settings-button"
            >
              <span>Manage Billing</span>
            </a>
          ) : (
            <button
              onClick={() => {
                const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
                const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
                
                if (hasStripeCustomerId && !hasActiveSubscription) {
                  // User has cancelled subscription - create reactivation checkout
                  createReactivationCheckoutSession(userStore.idToken, {}, '/add')
                    .then(response => {
                      if (response.data.url) {
                        window.location.href = response.data.url;
                      } else {
                        window.location.href = '/onboarding/choosevoice';
                      }
                    })
                    .catch(() => {
                      window.location.href = '/onboarding/choosevoice';
                    });
                } else {
                  // User has no subscription - send to onboarding
                  window.location.href = '/onboarding/choosevoice';
                }
              }}
              className="settings-button primary"
            >
              <span>Upgrade Account</span>
            </button>
          )}

          <button onClick={logOut} className="settings-button danger">
            <PowerSettingsNewIcon />
            <span>Log Out</span>
          </button>
        </div>
      </div>

      {deletedData && <div className="deleted-notice">Account removed. Logging out...</div>}

      {/* Referral Share Modal */}
      <ShareModal
        isOpen={isReferralShareModalOpen}
        onClose={() => setIsReferralShareModalOpen(false)}
        referral={{
          userId: user.user_id
        }}
      />
    </div>
  );
});

export default Settings; 